import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, CircularProgress,
} from '@mui/material';
import axios from '../../api/axios';
import { useAuth } from '../../contexts/AuthContext';
import { useMessage } from '../../contexts/MessageContext';

const CONSENT_VERSION = '2026-05-28';

const PrivacyConsentModal: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout, refreshUser } = useAuth();
  const { showApiError } = useMessage();
  const [loading, setLoading] = useState(false);

  // 已同意或尚未載入 user 時不顯示
  if (!user || user.privacy_consent_at) return null;

  const handleAgree = async () => {
    setLoading(true);
    try {
      await axios.post(`/api/system/privacy-consent?version=${CONSENT_VERSION}`);
      await refreshUser();
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    await logout();
  };

  return (
    <Dialog open maxWidth="sm" fullWidth disableEscapeKeyDown scroll="paper">
      <DialogTitle sx={{ fontWeight: 700 }}>
        {t('legal.consent.title')}
      </DialogTitle>
      {/* dividers：內容過長時於 modal 內 scroll，標題與下方按鈕固定可見（修「宣言部分無法點選」= 按鈕被內容擠出視窗） */}
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('legal.consent.intro')}
        </Typography>
        <Box
          sx={{
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 1,
            p: 2,
            mb: 2,
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {t('legal.consent.summary')}
          </Typography>
        </Box>
        <Typography variant="body2">
          <Button
            component={Link}
            to="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            sx={{ p: 0, minWidth: 0, textTransform: 'none', fontSize: 'inherit' }}
          >
            {t('legal.consent.readFull')}
          </Button>
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleDecline}
          disabled={loading}
        >
          {t('legal.consent.decline')}
        </Button>
        <Button
          variant="contained"
          onClick={handleAgree}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('legal.consent.agree')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrivacyConsentModal;
